/**
 * ChatPanel — wires ChatView to the Worker `POST /chat` endpoint.
 *
 * Handles authentication (Bearer token from AuthContext), error propagation,
 * and the network call. ChatView is purely presentational.
 *
 * When the Worker response includes `mealPlan`, ChatPanel wraps it into a
 * `ChatMessageContent` of kind "plan" so ChatView renders a MealPlanCard.
 * When the response includes `foodLogEntry`, it wraps it as kind "foodLog".
 * When the response includes `evolutionPoint`, it wraps it as kind "evolution".
 * When the response includes `replanProposal`, it wraps it as kind "proposal".
 * When the response is text-only (conversational / profile-collection),
 * it wraps it as kind "text".
 *
 * Spec §8 — MealPlanCard UI; Design §Data Flow.
 * Fase 3 — Tracking wiring (T5 wiring).
 */

import { useCallback } from "react";
import { useAuth } from "../auth/AuthContext.js";
import { ChatView } from "./ChatView.js";
import type { ChatMessageContent, ConversationTurn } from "./ChatView.js";
import type { Citation } from "./CitationList.js";
import type { MealPlan, FoodLogEntry, EvolutionPoint, ReplanProposal } from "@autodieta/shared";

export interface ChatPanelProps {
  workerUrl: string;
}

/** Shape of the /chat endpoint response (Fase 2 + Fase 3 contract). */
interface WorkerChatResponse {
  /** Conversational text from the LLM. */
  message?: string;
  /** Present when the engine produced a plan. */
  mealPlan?: MealPlan;
  /** Present when the LLM emitted a FOODLOG block and it was persisted (Fase 3). */
  foodLogEntry?: FoodLogEntry;
  /** Present when the LLM emitted a WEIGHT block and it was persisted (Fase 3). */
  evolutionPoint?: EvolutionPoint;
  /**
   * Present when a re-planning trigger fired and a proposal was created (Fase 3).
   * status is always "proposed" — never auto-applied (propose-not-apply invariant).
   */
  replanProposal?: ReplanProposal;
  /**
   * Present when the response is grounded in external evidence (Phase 4 — D-CITE).
   * citations[] contains source metadata for rendering via CitationList.
   * These are display/audit data only — never influence engine computation (D4).
   */
  citations?: Citation[];
  /** Legacy field — Fase 0 worker used "text" or "reply". */
  text?: string;
  reply?: string;
}

export function ChatPanel({ workerUrl }: ChatPanelProps): JSX.Element {
  const { getIdToken } = useAuth();

  const sendMessage = useCallback(
    async (text: string, history: ConversationTurn[]): Promise<ChatMessageContent> => {
      const idToken = await getIdToken();
      if (!idToken) throw new Error("No ID token available.");

      const base = workerUrl.replace(/\/+$/, "");
      const res = await fetch(`${base}/chat`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${idToken}`,
        },
        // BUG-1 fix: include prior conversation turns so the LLM has multi-turn context.
        // The worker's handleChat already accepts messages:[{role,content}] and forwards them
        // to handleChatRequest — it was just never sent from the frontend until now.
        body: JSON.stringify({ message: text, messages: history }),
      });

      if (!res.ok) {
        throw new Error(`Worker error: ${res.status}`);
      }

      const data = (await res.json()) as WorkerChatResponse;

      // Extract conversational text from all possible field names
      const messageText = data.message ?? data.text ?? data.reply ?? "";

      // If the response includes a mealPlan, return a "plan" content type
      if (data.mealPlan) {
        return {
          kind: "plan",
          text: messageText,
          mealPlan: data.mealPlan,
        };
      }

      // Fase 3: food log confirmed
      if (data.foodLogEntry) {
        return {
          kind: "foodLog",
          text: messageText,
          foodLogEntry: data.foodLogEntry,
        };
      }

      // Fase 3: evolution point registered
      if (data.evolutionPoint) {
        return {
          kind: "evolution",
          text: messageText,
          evolutionPoint: data.evolutionPoint,
        };
      }

      // Fase 3: re-plan proposal (propose-not-apply — status always "proposed")
      if (data.replanProposal) {
        return {
          kind: "proposal",
          text: messageText,
          replanProposal: data.replanProposal,
        };
      }

      // Phase 4 (D-CITE): grounded response — AI reply backed by external evidence (RAG/web).
      // When citations are present, render as kind:"grounded" so ChatView shows CitationList.
      // citations[] is display/audit data only — never influences engine computation (D4).
      if (data.citations != null && data.citations.length > 0) {
        return {
          kind: "grounded",
          text: messageText,
          citations: data.citations,
        };
      }

      // Otherwise, return plain text
      return {
        kind: "text",
        text: messageText,
      };
    },
    [getIdToken, workerUrl],
  );

  return <ChatView sendMessage={sendMessage} />;
}
